import type { InfiniteData } from "@tanstack/react-query";
import type { ChatMessage, ChatMessagesPage, ChatReaction, Conversation } from "../model/types";

// KAN-1342: чистые правки кэша чата. События хаба и мутации меняют кэш только через них.
// pages[0] — самая свежая страница (запрос без before), внутри страницы — хронология.

export type MessagesData = InfiniteData<ChatMessagesPage>;

/** Хронологический список сообщений из страниц бесконечного запроса. */
export function flattenPages(data: MessagesData | undefined): ChatMessage[] {
  if (!data) return [];
  return [...data.pages].reverse().flatMap((p) => p.messages);
}

/** Ничего не поменялось (событие про незагруженное сообщение, всё уже прочитано) — тот же объект: лента не перерисовывается. */
function mapMessages(data: MessagesData, fn: (m: ChatMessage) => ChatMessage): MessagesData {
  let changed = false;
  const pages = data.pages.map((p) => {
    let pageChanged = false;
    const messages = p.messages.map((m) => {
      const next = fn(m);
      if (next !== m) pageChanged = true;
      return next;
    });
    if (!pageChanged) return p;
    changed = true;
    return { ...p, messages };
  });
  return changed ? { ...data, pages } : data;
}

/**
 * Добавляет сообщение в конец свежей страницы. Повтор (эхо хаба после ответа POST) не дублирует,
 * а заменяет; оптимистичная копия (replaceId — её временный id) заменяется подтверждённой.
 */
export function upsertMessage(data: MessagesData | undefined, message: ChatMessage, replaceId?: string): MessagesData | undefined {
  if (!data || data.pages.length === 0) return data;
  const exists = data.pages.some((p) => p.messages.some((m) => m.id === message.id || m.id === replaceId));
  if (exists) {
    // И оптимистичная копия, и эхо хаба могут уже лежать в кэше — после замены остаётся одна.
    const seen = new Set<string>();
    const replaced = mapMessages(data, (m) => (m.id === message.id || m.id === replaceId ? message : m));
    return {
      ...replaced,
      pages: replaced.pages.map((p) => ({
        ...p,
        messages: p.messages.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true))),
      })),
    };
  }
  const [fresh, ...rest] = data.pages;
  return { ...data, pages: [{ ...fresh!, messages: [...fresh!.messages, message] }, ...rest] };
}

export function removeMessage(data: MessagesData | undefined, messageId: string): MessagesData | undefined {
  if (!data) return data;
  return { ...data, pages: data.pages.map((p) => ({ ...p, messages: p.messages.filter((m) => m.id !== messageId) })) };
}

export function markDeletedForAll(data: MessagesData | undefined, messageId: string): MessagesData | undefined {
  if (!data) return data;
  return mapMessages(data, (m) => (m.id === messageId ? { ...m, isDeleted: true, body: "", attachments: [], payloadJson: null } : m));
}

/** KAN-1338: правка — новый текст и метка «изм.»; сообщение остаётся на своём месте. */
export function applyEdit(data: MessagesData | undefined, messageId: string, body: string, editedAtUtc: string | null): MessagesData | undefined {
  if (!data) return data;
  return mapMessages(data, (m) => (m.id === messageId ? { ...m, body, editedAtUtc } : m));
}

/** KAN-1340: из сообщения убрали одно вложение; если больше ничего нет — сообщение считается удалённым у всех. */
export function removeAttachment(data: MessagesData | undefined, messageId: string, attachmentId: string): MessagesData | undefined {
  if (!data) return data;
  return mapMessages(data, (m) => {
    if (m.id !== messageId) return m;
    const attachments = m.attachments.filter((a) => a.id !== attachmentId);
    if (attachments.length === 0 && !m.body.trim()) return { ...m, isDeleted: true, body: "", attachments: [], payloadJson: null };
    return { ...m, attachments };
  });
}

/** KAN-1329: реакции сообщения заменяются снимком (с сервера или оптимистичным). */
export function applyReactions(data: MessagesData | undefined, messageId: string, reactions: ChatReaction[]): MessagesData | undefined {
  if (!data) return data;
  return mapMessages(data, (m) => (m.id === messageId ? { ...m, reactions } : m));
}

/** Собеседник прочитал всё, отправленное не позже readAtUtc: ставим галочки своим сообщениям. */
export function markReadUpTo(data: MessagesData | undefined, myUserId: string, readAtUtc: string): MessagesData | undefined {
  if (!data) return data;
  const limit = Date.parse(readAtUtc);
  return mapMessages(data, (m) =>
    m.senderId === myUserId && !m.isRead && Date.parse(m.sentAtUtc) <= limit ? { ...m, isRead: true, isDelivered: true } : m,
  );
}

/**
 * Новое сообщение поднимает беседу наверх списка и обновляет превью. Непрочитанное растёт,
 * только если сообщение чужое и беседа не открыта. false — беседы в списке нет, нужен перезапрос.
 */
export function applyMessageToList(
  list: Conversation[] | undefined,
  message: ChatMessage,
  opts: { myUserId: string; isOpen: boolean; preview: string },
): { list: Conversation[]; found: boolean } | undefined {
  if (!list) return undefined;
  const idx = list.findIndex((c) => c.id === message.conversationId);
  if (idx < 0) return { list, found: false };
  const current = list[idx]!;
  const incoming = message.senderId !== opts.myUserId;
  const updated: Conversation = {
    ...current,
    lastMessage: {
      id: message.id,
      senderId: message.senderId,
      senderDisplayName: message.senderDisplayName,
      preview: opts.preview,
      sentAtUtc: message.sentAtUtc,
      isDeleted: false,
    },
    unreadCount: incoming && !opts.isOpen ? current.unreadCount + 1 : current.unreadCount,
  };
  return { list: [updated, ...list.slice(0, idx), ...list.slice(idx + 1)], found: true };
}
